import { useState } from 'react'
import { router } from '@inertiajs/react'

import { csrfToken } from '../../../utils/csrfToken'
import { MoneyInput, formatMoney } from '../../../components/MoneyInput'

function AllocationCounter({ dividendsPercentage, groups }) {
  const divPct = parseFloat(dividendsPercentage) || 0
  const groupsPct = groups.reduce((sum, g) => sum + (parseFloat(g.budget_percentage) || 0), 0)
  const total = divPct + groupsPct
  const remaining = 100 - total
  const isComplete = total === 100

  return (
    <div className={`card mb-4 ${isComplete ? 'border-success' : remaining < 0 ? 'border-danger' : 'border-warning'}`}>
      <div className="card-body">
        <div className="row align-items-center">
          <div className="col-md-3">
            <div className="small text-body-secondary">Dividends</div>
            <div className="fw-semibold">{divPct.toFixed(2)}%</div>
          </div>
          <div className="col-md-3">
            <div className="small text-body-secondary">Groups</div>
            <div className="fw-semibold">{groupsPct.toFixed(2)}%</div>
          </div>
          <div className="col-md-3">
            <div className="small text-body-secondary">Total Allocated</div>
            <div className={`fw-semibold ${isComplete ? 'text-success' : remaining < 0 ? 'text-danger' : 'text-warning'}`}>
              {total.toFixed(2)}%
            </div>
          </div>
          <div className="col-md-3">
            <div className="small text-body-secondary">Remaining</div>
            <div className={`fw-semibold ${isComplete ? 'text-success' : remaining < 0 ? 'text-danger' : 'text-warning'}`}>
              {remaining > 0 ? `+${remaining.toFixed(2)}%` : `${remaining.toFixed(2)}%`}
            </div>
          </div>
        </div>
        {!isComplete ? (
          <div className={`mt-2 small ${remaining < 0 ? 'text-danger' : 'text-warning'}`}>
            {remaining > 0
              ? `Add ${remaining.toFixed(2)}% more to reach 100%`
              : `Over-allocated by ${Math.abs(remaining).toFixed(2)}% — reduce dividends or group budgets`}
          </div>
        ) : (
          <div className="mt-2 small text-success">
            Allocation is complete at 100%
          </div>
        )}
      </div>
    </div>
  )
}

function FlashAlert({ variant, message, onDismiss, textDark }) {
  if (!message) return null

  const className = [
    'alert',
    `alert-${variant}`,
    textDark ? 'text-dark' : '',
    'alert-dismissible',
    'fade',
    'show',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={className} role="alert">
      <h4 className="alert-heading mb-0">{message}</h4>
      <button
        type="button"
        className="btn-close"
        data-bs-dismiss="alert"
        aria-label="Close"
        onClick={onDismiss}
      ></button>
    </div>
  )
}

function ApproverList({ approvers }) {
  if (!approvers || approvers.length === 0) {
    return <span className="text-muted">—</span>
  }

  return (
    <div className="avatar-group d-inline-flex align-items-center">
      {approvers.map((approval, index) => (
        <span
          key={index}
          className="avatar avatar-xs"
          aria-label={approval.user?.full_name || approval.user?.email}
          data-bs-toggle="tooltip"
          data-bs-title={approval.user?.full_name || approval.user?.email}
        >
          <img
            src={approval.user?.avatar_url}
            className="avatar-img rounded-circle"
            alt={approval.user?.full_name || approval.user?.email}
          />
        </span>
      ))}
    </div>
  )
}

function GroupCard({ group, index, companyUsers, canEdit, onChange, onRemove, onAddMember, onChangeMember, onRemoveMember }) {
  const budgetPct = parseFloat(group.budget_percentage) || 0
  const members = group.members || []
  const totalPct = members.reduce((sum, m) => sum + (parseFloat(m.percentage) || 0), 0)
  const groupBudget = (parseFloat(group.budget_amount) || 0)
  const amountPerPct = totalPct > 0 ? groupBudget / totalPct : 0

  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">Group {index + 1}</h6>
          {canEdit ? (
            <button type="button" className="btn btn-sm btn-danger" onClick={() => onRemove(index)}>
              Remove Group
            </button>
          ) : null}
        </div>

        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="form-label">Name</label>
            {canEdit ? (
              <input
                type="text"
                className="form-control"
                value={group.name || ''}
                onChange={(e) => onChange(index, 'name', e.target.value)}
              />
            ) : (
              <div className="form-control-plaintext fw-semibold">{group.name || '—'}</div>
            )}
          </div>
          <div className="col-md-6">
            <label className="form-label">Budget (%)</label>
            {canEdit ? (
              <div className="input-group">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-control"
                  value={group.budget_percentage || ''}
                  onChange={(e) => onChange(index, 'budget_percentage', e.target.value)}
                />
                <span className="input-group-text">%</span>
              </div>
            ) : (
              <div className="form-control-plaintext">{budgetPct}%</div>
            )}
          </div>
        </div>

        <div className="mb-2">
          <span className="small text-body-secondary">Group Budget</span>
          <div className="fw-semibold">{formatMoney(groupBudget)}</div>
        </div>

        <div className="table-responsive">
          <table className="table table-sm table-hover table-nowrap card-table">
            <thead>
              <tr>
                <th>User</th>
                <th className="text-end">Percentage</th>
                <th className="text-end">Amount</th>
                {canEdit ? <th style={{ width: 40 }} /> : null}
              </tr>
            </thead>
            <tbody>
              {members.map((member, mindex) => {
                const pct = parseFloat(member.percentage) || 0
                const amount = pct * amountPerPct
                return (
                  <tr key={mindex}>
                    <td>
                      {canEdit ? (
                        <select
                          className="form-select form-select-sm"
                          value={member.user_id || ''}
                          onChange={(e) => onChangeMember(index, mindex, 'user_id', e.target.value)}
                        >
                          <option value="">Select user…</option>
                          {companyUsers.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.full_name || u.email}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="d-flex align-items-center">
                          {member.user?.avatar_url ? (
                            <img
                              src={member.user.avatar_url}
                              className="avatar avatar-xs avatar-img rounded-circle me-2"
                              alt={member.user.full_name || member.user.email}
                            />
                          ) : null}
                          <span>{member.user?.full_name || member.user?.email || '—'}</span>
                        </div>
                      )}
                    </td>
                    <td className="text-end">
                      {canEdit ? (
                        <div className="input-group input-group-sm">
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            className="form-control"
                            value={member.percentage || ''}
                            onChange={(e) => onChangeMember(index, mindex, 'percentage', e.target.value)}
                          />
                          <span className="input-group-text">%</span>
                        </div>
                      ) : (
                        <span>{pct}%</span>
                      )}
                    </td>
                    <td className="text-end">{formatMoney(amount)}</td>
                    {canEdit ? (
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger p-0"
                          onClick={() => onRemoveMember(index, mindex)}
                        >
                          Remove
                        </button>
                      </td>
                    ) : null}
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr>
                <td className="fw-semibold">Total</td>
                <td className="text-end fw-semibold">{totalPct.toFixed(2)}</td>
                <td className="text-end fw-semibold">{formatMoney(groupBudget)}</td>
                {canEdit ? <td /> : null}
              </tr>
              <tr>
                <td colSpan={canEdit ? 4 : 3} className="text-end text-body-secondary">
                  Amount per 1%: {amountPerPct > 0 ? formatMoney(amountPerPct) : '—'}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {canEdit ? (
          <button type="button" className="btn btn-sm btn-primary" onClick={() => onAddMember(index)}>
            Add Member
          </button>
        ) : null}
      </div>
    </div>
  )
}

export default function Show({ bonus, executives, companyUsers, gisRecord, canEdit, notice, alert, errors }) {
  const [flashNotice, setFlashNotice] = useState(notice)
  const [flashAlert, setFlashAlert] = useState(alert)
  const [saving, setSaving] = useState(false)
  const [finalizing, setFinalizing] = useState(false)

  const [name, setName] = useState(bonus.name || '')
  const [totalAmount, setTotalAmount] = useState(String(bonus.total_amount || ''))
  const [dividendsPercentage, setDividendsPercentage] = useState(String(bonus.dividends_percentage || ''))
  const [selectedApprovers, setSelectedApprovers] = useState(
    (bonus.approvers || []).map((a) => a.user?.id).filter(Boolean)
  )
  const [groups, setGroups] = useState(
    (bonus.groups || []).map((g) => ({
      name: g.name || '',
      budget_percentage: g.budget_percentage != null ? String(g.budget_percentage) : '',
      members: (g.members || []).map((m) => ({
        user_id: m.user?.id || m.user_id || '',
        percentage: m.percentage != null ? String(m.percentage) : '100',
      })),
    }))
  )

  const normalizedErrors = errors && Object.keys(errors).length ? errors : null
  const [fieldErrors, setFieldErrors] = useState(normalizedErrors)

  const approverIds = new Set(selectedApprovers)

  function toggleApprover(id) {
    setSelectedApprovers((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return Array.from(next)
    })
  }

  function addGroup() {
    setGroups((prev) => [
      ...prev,
      { name: '', budget_percentage: '', members: [{ user_id: '', percentage: '100' }] },
    ])
  }

  function removeGroup(index) {
    setGroups((prev) => prev.filter((_, i) => i !== index))
  }

  function updateGroup(index, field, value) {
    setGroups((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }

  function addMember(groupIndex) {
    setGroups((prev) => {
      const next = [...prev]
      next[groupIndex] = {
        ...next[groupIndex],
        members: [...next[groupIndex].members, { user_id: '', percentage: '100' }],
      }
      return next
    })
  }

  function updateMember(groupIndex, memberIndex, field, value) {
    setGroups((prev) => {
      const next = [...prev]
      next[groupIndex].members[memberIndex] = {
        ...next[groupIndex].members[memberIndex],
        [field]: value,
      }
      return next
    })
  }

  function removeMember(groupIndex, memberIndex) {
    setGroups((prev) => {
      const next = [...prev]
      next[groupIndex] = {
        ...next[groupIndex],
        members: next[groupIndex].members.filter((_, i) => i !== memberIndex),
      }
      return next
    })
  }

  const totalBonus = parseFloat(totalAmount) || 0
  const percentage = parseFloat(dividendsPercentage) || 0
  const dividendsAmount = totalBonus * (percentage / 100)

  const shareholders = (gisRecord?.shareholders || [])
    .map((s) => ({ ...s, shares_count: s.shares_count || 0 }))
    .sort((a, b) => b.shares_count - a.shares_count)
  const totalShares = shareholders.reduce((sum, s) => sum + s.shares_count, 0)
  const dividendPerShare = totalShares > 0 ? dividendsAmount / totalShares : 0

  async function submit(e) {
    e.preventDefault()
    setSaving(true)
    setFlashAlert(null)
    setFieldErrors(null)

    const approversPayload = selectedApprovers.map((id) => ({ user_id: id, approved_at: null }))
    const groupsPayload = groups.map((g) => ({
      name: g.name,
      budget_percentage: g.budget_percentage,
      members: g.members.map((m) => ({
        user_id: m.user_id,
        percentage: m.percentage ? parseFloat(m.percentage) : 100,
      })),
    }))

    const response = await fetch(`/executives/bonuses/${bonus.id}`, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': csrfToken() || '',
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        bonus: {
          name,
          total_amount: totalAmount,
          dividends_percentage: dividendsPercentage,
          approvers: approversPayload,
          groups: groupsPayload,
        },
      }),
      credentials: 'same-origin',
    })

    if (response.status === 200 || response.status === 204 || response.redirected) {
      router.visit(`/executives/bonuses/${bonus.id}`)
      return
    }

    if (response.status === 422) {
      const payload = await response.json()
      const nextErrors = payload.errors && Object.keys(payload.errors).length ? payload.errors : null
      setFieldErrors(nextErrors)
      if (payload.message) {
        setFlashAlert(payload.message)
      }
      setSaving(false)
      return
    }

    setFlashAlert(`Failed to update bonus: ${response.status}`)
    setSaving(false)
  }

  async function handleFinalize() {
    if (!window.confirm('Are you sure you want to finalize this bonus?')) return
    setFinalizing(true)
    setFlashAlert(null)

    const response = await fetch(`/executives/bonuses/${bonus.id}/finalize`, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': csrfToken() || '',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
    })

    if (response.ok || response.redirected) {
      router.visit(`/executives/bonuses/${bonus.id}`)
      return
    }

    setFlashAlert('Failed to finalize bonus.')
    setFinalizing(false)
  }

  return (
    <div className="container-fluid">
      <div className="header">
        <div className="header-body">
          <div className="row align-items-end">
            <div className="col">
              <h6 className="header-pretitle">Executives</h6>
              <h1 className="header-title">{bonus.name}</h1>
            </div>
            <div className="col-auto d-flex gap-2">
              {canEdit ? (
                <button
                  type="button"
                  className="btn btn-success"
                  disabled={finalizing}
                  onClick={handleFinalize}
                >
                  {finalizing ? 'Finalizing…' : 'Finalize Bonus'}
                </button>
              ) : null}
              <button
                type="button"
                className="btn btn-link"
                onClick={() => router.visit('/executives/bonuses')}
              >
                &lt; Back
              </button>
            </div>
          </div>
        </div>
      </div>

      <FlashAlert variant="danger" message={flashAlert} onDismiss={() => setFlashAlert(null)} />
      <FlashAlert variant="success" message={flashNotice} textDark onDismiss={() => setFlashNotice(null)} />

      {canEdit ? (
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10 col-xl-8">
            <AllocationCounter
              dividendsPercentage={dividendsPercentage}
              groups={groups}
            />
          </div>
        </div>
      ) : null}

      <div className="row justify-content-center">
        <div className="col-12 col-lg-10 col-xl-8">
          <form onSubmit={submit}>
            <div className="card">
              <div className="card-header">
                <h4 className="card-header-title">Bonus Details</h4>
              </div>
              <div className="card-body">
                {fieldErrors ? (
                  <div className="alert alert-danger">
                    <div className="mb-2">Bonus was not saved</div>
                    <ul className="mb-0">
                      {Object.values(fieldErrors).flat().map((msg, i) => (
                        <li key={i}>{msg}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <div className="form-group">
                  <label className="form-label">Name</label>
                  {canEdit ? (
                    <>
                      <input
                        type="text"
                        className="form-control"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                      {fieldErrors?.name ? (
                        <div className="invalid-feedback d-block">{fieldErrors.name.join(', ')}</div>
                      ) : null}
                    </>
                  ) : (
                    <div className="form-control-plaintext">{bonus.name}</div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Status</label>
                  <div>
                    <span className={`badge bg-${bonus.status === 'finalized' ? 'success' : 'secondary'}`}>
                      {bonus.status}
                    </span>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Total Bonuses to be Distributed</label>
                  {canEdit ? (
                    <>
                      <MoneyInput
                        value={totalAmount}
                        onChange={setTotalAmount}
                        inputClassName="form-control"
                      />
                      {fieldErrors?.total_amount ? (
                        <div className="invalid-feedback d-block">{fieldErrors.total_amount.join(', ')}</div>
                      ) : null}
                    </>
                  ) : (
                    <div className="form-control-plaintext">{formatMoney(bonus.total_amount)}</div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Approvers</label>
                  {canEdit ? (
                    <div className="table-responsive">
                      <table className="table table-sm table-hover table-nowrap">
                        <thead>
                          <tr>
                            <th style={{ width: 40 }} />
                            <th>Executive</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(executives || []).map((exec) => (
                            <tr key={exec.id}>
                              <td>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={approverIds.has(exec.id)}
                                  onChange={() => toggleApprover(exec.id)}
                                />
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="avatar avatar-sm me-2">
                                    <img
                                      src={exec.avatar_url}
                                      className="avatar-img rounded-circle"
                                      alt={exec.full_name || exec.email}
                                    />
                                  </div>
                                  <div>
                                    <div className="fw-semibold">{exec.full_name || exec.email}</div>
                                    <div className="small text-body-secondary">{exec.email}</div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))}
                          {!executives?.length ? (
                            <tr>
                              <td colSpan={2} className="text-center text-body-secondary py-4">
                                No executives found.
                              </td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="form-control-plaintext">
                      <ApproverList approvers={bonus.approvers} />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {bonus.status === 'draft' && gisRecord ? (
              <div className="card mt-4">
                <div className="card-header">
                  <h4 className="card-header-title">Dividends</h4>
                </div>
                <div className="card-body">
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <div className="mb-2">
                        <span className="small text-body-secondary">Total Bonus</span>
                        <div className="fw-semibold">{formatMoney(totalBonus)}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-2">
                        <label className="form-label">Dividends Budget (%)</label>
                        {canEdit ? (
                          <>
                            <div className="input-group">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                className="form-control"
                                value={dividendsPercentage}
                                onChange={(e) => setDividendsPercentage(e.target.value)}
                              />
                              <span className="input-group-text">%</span>
                            </div>
                            {fieldErrors?.dividends_percentage ? (
                              <div className="invalid-feedback d-block">{fieldErrors.dividends_percentage.join(', ')}</div>
                            ) : null}
                          </>
                        ) : (
                          <div className="form-control-plaintext">{bonus.dividends_percentage}%</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <span className="small text-body-secondary">Dividends</span>
                    <div className="fw-semibold">{formatMoney(dividendsAmount)}</div>
                  </div>

                  {shareholders.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-sm table-hover table-nowrap card-table">
                        <thead>
                          <tr>
                            <th>Shareholder</th>
                            <th className="text-end">Shares</th>
                            <th className="text-end">Dividend</th>
                          </tr>
                        </thead>
                        <tbody>
                          {shareholders.map((s, index) => (
                            <tr key={index}>
                              <td className="fw-semibold">{s.name || '—'}</td>
                              <td className="text-end">{s.shares_count.toLocaleString()}</td>
                              <td className="text-end">{formatMoney(s.shares_count * dividendPerShare)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td className="fw-semibold">Total</td>
                            <td className="text-end fw-semibold">{totalShares.toLocaleString()}</td>
                            <td className="text-end fw-semibold">{formatMoney(dividendsAmount)}</td>
                          </tr>
                          <tr>
                            <td colSpan={3} className="text-end text-body-secondary">
                              Dividend Per Share: {dividendPerShare > 0 ? formatMoney(dividendPerShare) : '—'}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center text-body-secondary py-4">
                      No shareholders found in GIS.
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            <div className="card mt-4">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h4 className="card-header-title mb-0">Bonus Groups</h4>
                  {canEdit ? (
                    <button type="button" className="btn btn-primary" onClick={addGroup}>
                      Add Group
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="card-body">
                {groups.map((group, index) => {
                  const groupBudget = totalBonus * ((parseFloat(group.budget_percentage) || 0) / 100)
                  const members = group.members || []
                  const totalPct = members.reduce((sum, m) => sum + (parseFloat(m.percentage) || 0), 0)
                  const amountPerPct = totalPct > 0 ? groupBudget / totalPct : 0

                  return (
                    <GroupCard
                      key={index}
                      group={{
                        ...group,
                        budget_amount: groupBudget,
                        members: members.map((m) => {
                          const user = companyUsers.find((u) => u.id === m.user_id)
                          const pct = parseFloat(m.percentage) || 0
                          return {
                            user_id: m.user_id,
                            user: user || null,
                            percentage: pct,
                            amount: pct * amountPerPct,
                          }
                        }),
                      }}
                      index={index}
                      companyUsers={companyUsers}
                      canEdit={canEdit}
                      onChange={updateGroup}
                      onRemove={removeGroup}
                      onAddMember={addMember}
                      onChangeMember={updateMember}
                      onRemoveMember={removeMember}
                    />
                  )
                })}

                {!groups.length ? (
                  <div className="text-center text-body-secondary py-4">
                    No groups defined.
                  </div>
                ) : null}

                {groups.length > 0 ? (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between">
                      <span className="fw-semibold">Total Allocated</span>
                      <span className="fw-semibold">
                        {formatMoney(
                          groups.reduce((sum, g) => {
                            return sum + totalBonus * ((parseFloat(g.budget_percentage) || 0) / 100)
                          }, 0)
                        )}
                      </span>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            {canEdit ? (
              <div className="form-group mt-4">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            ) : null}
          </form>
        </div>
      </div>
    </div>
  )
}
